import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { format } from 'date-fns'
import { supabase } from '../../lib/supabase'
import {
  getDiscussions,
  getDiscussionComments,
  createDiscussion,
  createComment,
  toggleDiscussionVote,
  toggleCommentVote,
  getUserVotes
} from '../../lib/supabase'
import {
  MessageSquare,
  TrendingUp,
  Clock,
  MessageCircle,
  Share2,
  Bookmark,
  MoreHorizontal,
  Image,
  Link as LinkIcon,
  Send,
  Search,
  Car,
  Wrench,
  Trophy,
  ShoppingBag,
  HelpCircle,
  Flame,
  Plus,
  ArrowUp,
  X,
  Loader,
  AlertCircle
} from 'lucide-react'

// Discussion categories
const CATEGORIES = [
  { id: 'all', label: 'All Discussions', icon: MessageSquare, color: 'bg-rdc-primary' },
  { id: 'general', label: 'General', icon: MessageSquare, color: 'bg-blue-500', description: 'Car chat, introductions, anything goes' },
  { id: 'technical', label: 'Tech & DIY', icon: Wrench, color: 'bg-amber-500', description: 'Maintenance tips, how-tos, troubleshooting' },
  { id: 'showcase', label: 'Showcase', icon: Car, color: 'bg-green-500', description: 'Show off your ride, new additions, progress' },
  { id: 'events', label: 'Events & Meets', icon: Trophy, color: 'bg-purple-500', description: 'Cars & coffee, track days, shows' },
  { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag, color: 'bg-pink-500', description: 'Parts, cars, and services' },
  { id: 'help', label: 'Help & Advice', icon: HelpCircle, color: 'bg-red-500', description: 'Questions, recommendations, guidance' }
]

// No mock data — community shows real posts only, or empty state

const getInitials = (name) => {
  if (!name) return '?'
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

const JustAddedCard = ({ vehicle }) => (
  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-rdc-orange/5 to-transparent rounded-xl border border-rdc-stone/20 flex-shrink-0 w-full sm:w-96">
    <div className="w-16 h-16 rounded-lg overflow-hidden bg-rdc-cream flex-shrink-0">
      {vehicle.primary_image_url ? (
        <img src={vehicle.primary_image_url} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Car size={24} className="text-rdc-stone" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-rdc-orange/10 text-rdc-orange font-medium">Just Added</span>
      </div>
      <p className="text-sm text-black font-medium truncate">
        {vehicle.year} {vehicle.make} {vehicle.model}
      </p>
      <p className="text-xs text-rdc-taupe">
        Added by {getDisplayName(vehicle.owner?.full_name)} • {formatRelativeTime(vehicle.created_at)}
      </p>
    </div>
  </div>
)

const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr)
  const seconds = Math.floor((new Date() - date) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return format(date, 'MMM d')
}

const formatRelativeTime = (dateStr) => {
  return formatTimeAgo(dateStr)
}

const getDisplayName = (fullName) => {
  if (!fullName) return 'A collector'
  const parts = fullName.split(' ')
  if (parts.length >= 2) return `${parts[0]} ${parts[1][0]}.`
  return parts[0]
}

const DiscussionCard = ({ discussion, onOpen, onVote, isVoted }) => {
  const category = CATEGORIES.find(c => c.id === discussion.category)
  const authorName = discussion.author?.full_name || 'Anonymous'
  const [saved, setSaved] = useState(false)

  return (
    <div className="card p-5 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-rdc-primary/20 flex items-center justify-center font-semibold text-rdc-primary">
            {getInitials(authorName)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-black">{authorName}</span>
              <span className="text-rdc-taupe">·</span>
              <span className="text-sm text-rdc-taupe">{formatTimeAgo(discussion.created_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full text-white ${category?.color || 'bg-gray-500'}`}>
                {category?.label || discussion.category}
              </span>
              {discussion.is_pinned && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                  <Flame size={10} />
                  Pinned
                </span>
              )}
            </div>
          </div>
        </div>
        <button className="p-2 text-rdc-taupe hover:text-rdc-dark-gray hover:bg-rdc-cream rounded-lg">
          <MoreHorizontal size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="cursor-pointer" onClick={() => onOpen(discussion)}>
        <h3 className="font-display text-lg font-semibold text-black mb-2 hover:text-rdc-primary">
          {discussion.title}
        </h3>
        <p className="text-rdc-dark-gray text-sm line-clamp-2 mb-3">
          {discussion.content}
        </p>

        {discussion.image_url && (
          <div className="mb-3 img-container aspect-card rounded-lg">
            <img src={discussion.image_url} alt="" />
          </div>
        )}

        {discussion.tags && discussion.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {discussion.tags.map(tag => (
              <span key={tag} className="text-xs px-2 py-1 bg-rdc-cream text-rdc-taupe rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-rdc-warm-gray/30">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onVote && onVote(discussion.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isVoted
                ? 'bg-rdc-primary text-white'
                : 'bg-rdc-cream text-rdc-dark-gray hover:bg-rdc-warm-gray/50'
            }`}
          >
            <ArrowUp size={16} />
            {discussion.upvote_count + (isVoted ? 1 : 0)}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onOpen(discussion)}
            className="flex items-center gap-1.5 text-sm text-rdc-taupe hover:text-rdc-primary"
          >
            <MessageCircle size={16} />
            {discussion.comment_count}
          </button>
          <button className="flex items-center gap-1.5 text-sm text-rdc-taupe hover:text-rdc-primary">
            <Share2 size={16} />
          </button>
          <button
            onClick={() => setSaved(!saved)}
            className={`p-1.5 rounded-lg ${saved ? 'text-rdc-primary' : 'text-rdc-taupe hover:text-rdc-primary'}`}
          >
            <Bookmark size={16} fill={saved ? 'currentColor' : 'none'} />
          </button>
        </div>
      </div>
    </div>
  )
}

const DiscussionModal = ({ discussion, onClose, user, useMock }) => {
  const [comment, setComment] = useState('')
  const [comments, setComments] = useState([])
  const [loadingComments, setLoadingComments] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [upvoted, setUpvoted] = useState(false)
  const category = CATEGORIES.find(c => c.id === discussion.category)
  const authorName = discussion.author?.full_name || 'Anonymous'

  useEffect(() => {
    loadComments()
  }, [discussion.id])

  const loadComments = async () => {
    setLoadingComments(true)
    if (useMock) {
      setComments([])
      setLoadingComments(false)
      return
    }
    const { data, error } = await getDiscussionComments(discussion.id)
    if (!error && data) {
      setComments(data)
    } else {
      setComments([])
    }
    setLoadingComments(false)
  }

  const handleSubmitComment = async () => {
    if (!comment.trim() || !user) return
    setSubmitting(true)

    if (useMock) {
      // Add to local state for mock mode
      const newComment = {
        id: `c${Date.now()}`,
        author: { full_name: user.user_metadata?.full_name || 'You', avatar_url: null },
        content: comment.trim(),
        upvote_count: 0,
        created_at: new Date().toISOString(),
        replies: []
      }
      setComments(prev => [newComment, ...prev])
      setComment('')
      setSubmitting(false)
      return
    }

    const { data, error } = await createComment({
      discussion_id: discussion.id,
      user_id: user.id,
      content: comment.trim()
    })

    if (!error && data) {
      setComments(prev => [...prev, data])
      setComment('')
    }
    setSubmitting(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmitComment()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 pb-10 px-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-rdc-warm-gray/30 p-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2.5 py-1 rounded-full text-white ${category?.color}`}>
              {category?.label}
            </span>
            {discussion.is_pinned && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                <Flame size={10} />
                Pinned
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-rdc-cream rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Author */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-rdc-primary/20 flex items-center justify-center font-semibold text-rdc-primary text-lg">
              {getInitials(authorName)}
            </div>
            <div>
              <div className="font-medium text-black">{authorName}</div>
              <div className="text-sm text-rdc-taupe">{formatTimeAgo(discussion.created_at)}</div>
            </div>
          </div>

          <h1 className="font-display text-2xl font-semibold text-black mb-4">
            {discussion.title}
          </h1>
          <p className="text-rdc-dark-gray leading-relaxed mb-4">
            {discussion.content}
          </p>

          {discussion.image_url && (
            <div className="mb-4 img-container aspect-hero rounded-xl">
              <img src={discussion.image_url} alt="" />
            </div>
          )}

          {discussion.tags && discussion.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {discussion.tags.map(tag => (
                <span key={tag} className="text-sm px-3 py-1 bg-rdc-cream text-rdc-dark-gray rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 py-4 border-y border-rdc-warm-gray/30">
            <button
              onClick={() => setUpvoted(!upvoted)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-colors ${
                upvoted
                  ? 'bg-rdc-primary text-white'
                  : 'bg-rdc-cream text-rdc-dark-gray hover:bg-rdc-warm-gray/50'
              }`}
            >
              <ArrowUp size={18} />
              {discussion.upvote_count + (upvoted ? 1 : 0)} upvotes
            </button>
            <button className="flex items-center gap-2 text-rdc-taupe hover:text-rdc-primary">
              <Share2 size={18} />
              Share
            </button>
            <button className="flex items-center gap-2 text-rdc-taupe hover:text-rdc-primary">
              <Bookmark size={18} />
              Save
            </button>
          </div>

          {/* Comments */}
          <div className="mt-6">
            <h3 className="font-display font-semibold text-black mb-4">
              {comments.length} Comments
            </h3>

            {/* Comment Input */}
            {user ? (
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-rdc-primary/20 flex items-center justify-center font-semibold text-rdc-primary flex-shrink-0">
                  {getInitials(user.user_metadata?.full_name)}
                </div>
                <div className="flex-1 relative">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Add to the discussion..."
                    className="input-field pr-12 min-h-[80px] resize-none"
                  />
                  <button
                    onClick={handleSubmitComment}
                    disabled={!comment.trim() || submitting}
                    className="absolute bottom-3 right-3 p-2 bg-rdc-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-rdc-primary/90 transition-colors"
                  >
                    {submitting ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-rdc-cream rounded-lg text-center">
                <p className="text-sm text-rdc-taupe">Sign in to join the discussion</p>
              </div>
            )}

            {/* Comments List */}
            {loadingComments ? (
              <div className="text-center py-8">
                <Loader size={24} className="animate-spin text-rdc-primary mx-auto" />
              </div>
            ) : (
              <div className="space-y-4">
                {comments.map(c => (
                  <div key={c.id} className="group">
                    <div className="flex gap-3">
                      <div className="w-9 h-9 rounded-full bg-rdc-cream flex items-center justify-center font-semibold text-rdc-taupe text-sm flex-shrink-0">
                        {getInitials(c.author?.full_name)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-black text-sm">{c.author?.full_name || 'Anonymous'}</span>
                          <span className="text-xs text-rdc-taupe">{formatTimeAgo(c.created_at)}</span>
                        </div>
                        <p className="text-rdc-dark-gray text-sm mb-2">{c.content}</p>
                        <div className="flex items-center gap-3 text-xs">
                          <button className="flex items-center gap-1 text-rdc-taupe hover:text-rdc-primary">
                            <ArrowUp size={14} />
                            {c.upvote_count}
                          </button>
                          <button className="text-rdc-taupe hover:text-rdc-primary">Reply</button>
                        </div>

                        {/* Nested Replies */}
                        {c.replies?.length > 0 && (
                          <div className="mt-3 pl-4 border-l-2 border-rdc-warm-gray/30 space-y-3">
                            {c.replies.map(reply => (
                              <div key={reply.id} className="flex gap-3">
                                <div className="w-7 h-7 rounded-full bg-rdc-cream flex items-center justify-center font-semibold text-rdc-taupe text-xs flex-shrink-0">
                                  {getInitials(reply.author?.full_name)}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-black text-sm">{reply.author?.full_name || 'Anonymous'}</span>
                                    <span className="text-xs text-rdc-taupe">{formatTimeAgo(reply.created_at)}</span>
                                  </div>
                                  <p className="text-rdc-dark-gray text-sm">{reply.content}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const CreatePostModal = ({ onClose, selectedCategory, user, useMock, onPostCreated }) => {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState(selectedCategory || 'general')
  const [tags, setTags] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) return
    setSubmitting(true)
    setError('')

    const tagsArray = tags.split(',').map(t => t.trim()).filter(Boolean)

    if (useMock) {
      const newPost = {
        id: `mock-${Date.now()}`,
        title: title.trim(),
        content: content.trim(),
        category,
        tags: tagsArray,
        author: { full_name: user?.user_metadata?.full_name || 'You', avatar_url: null },
        upvote_count: 0,
        comment_count: 0,
        is_pinned: false,
        created_at: new Date().toISOString()
      }
      onPostCreated(newPost)
      onClose()
      setSubmitting(false)
      return
    }

    const { data, error: createError } = await createDiscussion({
      user_id: user.id,
      title: title.trim(),
      content: content.trim(),
      category,
      tags: tagsArray
    })

    if (createError) {
      setError(createError.message || 'Failed to create post')
      setSubmitting(false)
      return
    }

    if (data) {
      onPostCreated(data)
    }
    onClose()
    setSubmitting(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 pb-10 px-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl font-semibold text-black">Start a Discussion</h2>
            <button onClick={onClose} className="p-2 hover:bg-rdc-cream rounded-lg">
              <X size={20} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle size={16} className="text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input-field"
              >
                {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input-field"
                placeholder="What's on your mind?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="input-field min-h-[150px] resize-none"
                placeholder="Share your thoughts, ask a question, or start a conversation..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Tags <span className="text-rdc-taupe font-normal">(comma separated)</span>
              </label>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="input-field"
                placeholder="e.g., Porsche, 911, Track Day"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button className="flex items-center gap-2 px-4 py-2 border border-rdc-warm-gray rounded-lg text-sm text-rdc-dark-gray hover:bg-rdc-cream">
                <Image size={18} />
                Add Image
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-rdc-warm-gray rounded-lg text-sm text-rdc-dark-gray hover:bg-rdc-cream">
                <LinkIcon size={18} />
                Add Link
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-rdc-warm-gray rounded-lg text-sm text-rdc-dark-gray hover:bg-rdc-cream">
                <Car size={18} />
                Link Vehicle
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-rdc-warm-gray/30">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={!title.trim() || !content.trim() || submitting}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && <Loader size={16} className="animate-spin" />}
              {submitting ? 'Posting...' : 'Post Discussion'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Community() {
  const { user } = useAuth()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [sortBy, setSortBy] = useState('hot')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDiscussion, setSelectedDiscussion] = useState(null)
  const [showCreatePost, setShowCreatePost] = useState(false)
  const [discussions, setDiscussions] = useState([])
  const [recentVehicles, setRecentVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [useMock, setUseMock] = useState(false)
  const [votedDiscussions, setVotedDiscussions] = useState(new Set())

  useEffect(() => {
    loadDiscussions()
    loadRecentVehicles()
  }, [selectedCategory, sortBy])

  const loadDiscussions = async () => {
    setLoading(true)
    const { data, error } = await getDiscussions(selectedCategory, sortBy)

    if (error || !data) {
      setUseMock(true)
      setDiscussions([])
    } else {
      setUseMock(false)
      setDiscussions(data)
    }
    setLoading(false)
  }

  const loadRecentVehicles = async () => {
    try {
      // Fetch recently added public vehicles for "Just Added" feed
      const { data, error } = await supabase
        .from('vehicles')
        .select('id, year, make, model, color, primary_image_url, created_at, owner:profiles!owner_id(full_name)')
        .eq('is_public', true)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(5)

      if (!error && data) {
        setRecentVehicles(data)
      }
    } catch (err) {
      // Silently fail - this is optional UI enhancement
      console.error('Failed to load recent vehicles:', err)
    }
  }

  const handleVote = async (discussionId) => {
    if (!user) return
    if (useMock) {
      setVotedDiscussions(prev => {
        const next = new Set(prev)
        if (next.has(discussionId)) next.delete(discussionId)
        else next.add(discussionId)
        return next
      })
      return
    }
    const { voted } = await toggleDiscussionVote(user.id, discussionId)
    setVotedDiscussions(prev => {
      const next = new Set(prev)
      if (voted) next.add(discussionId)
      else next.delete(discussionId)
      return next
    })
  }

  const handlePostCreated = (newPost) => {
    setDiscussions(prev => [newPost, ...prev])
  }

  // Client-side filtering for search
  const filteredDiscussions = discussions.filter(d => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return d.title?.toLowerCase().includes(query) ||
      d.content?.toLowerCase().includes(query) ||
      d.tags?.some(t => t.toLowerCase().includes(query))
  })

  // Sort (mock data needs client-side sort; DB data comes pre-sorted)
  const sortedDiscussions = useMock
    ? [...filteredDiscussions].sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1
        if (!a.is_pinned && b.is_pinned) return 1
        switch (sortBy) {
          case 'new':
            return new Date(b.created_at) - new Date(a.created_at)
          case 'top':
            return b.upvote_count - a.upvote_count
          case 'hot':
          default:
            const aScore = a.upvote_count + a.comment_count * 2
            const bScore = b.upvote_count + b.comment_count * 2
            const aAge = (Date.now() - new Date(a.created_at)) / (1000 * 60 * 60)
            const bAge = (Date.now() - new Date(b.created_at)) / (1000 * 60 * 60)
            return (bScore / Math.pow(bAge + 2, 1.5)) - (aScore / Math.pow(aAge + 2, 1.5))
        }
      })
    : filteredDiscussions

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="page-title">Community</h1>
          <p className="text-rdc-taupe mt-1">Connect, share, and discuss with fellow enthusiasts</p>
        </div>
        <button
          onClick={() => setShowCreatePost(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          New Post
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-rdc-taupe" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search discussions..."
          className="input-field pl-12"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar - Categories */}
        <div className="lg:col-span-1">
          <div className="card p-4 sticky top-4">
            <h3 className="font-display font-semibold text-black mb-3">Categories</h3>
            <div className="space-y-1">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-rdc-primary/10 text-rdc-primary'
                      : 'text-rdc-dark-gray hover:bg-rdc-cream'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center`}>
                    <cat.icon size={16} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{cat.label}</div>
                    {cat.description && selectedCategory === cat.id && (
                      <div className="text-xs text-rdc-taupe mt-0.5">{cat.description}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Recently Added Section */}
          {recentVehicles.length > 0 && (
            <div className="mb-6">
              <h3 className="font-display font-semibold text-black mb-3">Recently Added to the Community</h3>
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                {recentVehicles.map(vehicle => (
                  <JustAddedCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
            </div>
          )}

          {/* Sort Tabs */}
          <div className="flex items-center gap-1 mb-4 bg-rdc-cream rounded-lg p-1 w-fit">
            {[
              { id: 'hot', label: 'Hot', icon: Flame },
              { id: 'new', label: 'New', icon: Clock },
              { id: 'top', label: 'Top', icon: TrendingUp }
            ].map(sort => (
              <button
                key={sort.id}
                onClick={() => setSortBy(sort.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  sortBy === sort.id
                    ? 'bg-white text-black shadow-sm'
                    : 'text-rdc-taupe hover:text-rdc-dark-gray'
                }`}
              >
                <sort.icon size={16} />
                {sort.label}
              </button>
            ))}
          </div>

          {/* Discussions List */}
          {loading ? (
            <div className="text-center py-12">
              <Loader size={32} className="animate-spin text-rdc-primary mx-auto mb-3" />
              <p className="text-rdc-taupe">Loading discussions...</p>
            </div>
          ) : sortedDiscussions.length === 0 ? (
            <div className="text-center py-12 card">
              <MessageSquare size={48} className="mx-auto mb-4 text-rdc-warm-gray" />
              <h3 className="font-display text-lg font-semibold text-black mb-2">No discussions yet</h3>
              <p className="text-rdc-taupe mb-6">Be the first to start a conversation!</p>
              <button
                onClick={() => setShowCreatePost(true)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Plus size={18} />
                Start a Discussion
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedDiscussions.map(discussion => (
                <DiscussionCard
                  key={discussion.id}
                  discussion={discussion}
                  onOpen={setSelectedDiscussion}
                  onVote={handleVote}
                  isVoted={votedDiscussions.has(discussion.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Discussion Modal */}
      {selectedDiscussion && (
        <DiscussionModal
          discussion={selectedDiscussion}
          onClose={() => setSelectedDiscussion(null)}
          user={user}
          useMock={useMock}
        />
      )}

      {/* Create Post Modal */}
      {showCreatePost && (
        <CreatePostModal
          onClose={() => setShowCreatePost(false)}
          selectedCategory={selectedCategory !== 'all' ? selectedCategory : null}
          user={user}
          useMock={useMock}
          onPostCreated={handlePostCreated}
        />
      )}
    </div>
  )
}
